import type { ApplicationStatusCardProps } from './ApplicationStatusCard'
import type { UserStatus } from '../../hooks/useCurrentUser'
import type { MessageKey } from '../../i18n/messages'
import { ROUTES } from '../../config/constants'

/**
 * Sample application-status cards, one per step. Each carries its own
 * `viewDetailsTo` so the card's action button navigates to the right place for
 * that step, plus the `actionLabelKey` for that step's wording. Live per-user
 * values (assetId, cost) are merged in by useApplicationStatusCard; what remains
 * here is the step choreography that the BFF doesn't drive yet.
 */

// Fields the samples provide — everything except live data and the action.
type SampleStatus = Omit<
  ApplicationStatusCardProps,
  'actionLabel' | 'onAction' | 'assetId' | 'cost'
> & {
  /** Message key for the action button label on this step. */
  actionLabelKey: MessageKey
}

// Current step for the applicant: sign the NDA → /nda.
export const SAMPLE_APPLICATION_STATUS_NDA = {
  status: 'inProgress',
  loanName: 'Business Recoverable Grant',
  nextStepStatus: 'actionRequired',
  nextStep: 'Sign the NDA',
  viewDetailsTo: ROUTES.nda,
  actionLabelKey: 'applicationStatus.card.viewDetails',
} satisfies SampleStatus

// Next step: upload KYC documents → /apply (the EDA KYC form). The button reads
// "Continue" here — it resumes the upload rather than opening a detail view.
export const SAMPLE_APPLICATION_STATUS_KYC = {
  status: 'inProgress',
  loanName: 'Business Recoverable Grant',
  nextStepStatus: 'actionRequired',
  nextStep: 'Upload KYC documents',
  viewDetailsTo: ROUTES.apply,
  actionLabelKey: 'applicationStatus.card.continue',
} satisfies SampleStatus

/**
 * Which sample step to show for a given user status (status is the step proxy
 * until the BFF drives it): applicant is on the NDA step, participant on the KYC
 * step. Prospect has no status section, so it falls through to the NDA default.
 */
export function sampleApplicationStatusFor(status: UserStatus) {
  switch (status) {
    case 'participant':
      return SAMPLE_APPLICATION_STATUS_KYC
    case 'applicant':
    default:
      return SAMPLE_APPLICATION_STATUS_NDA
  }
}
